import * as React from 'react';
import './styles.css';
import { ScoutIcon } from '../icons/scout';

interface BettyConfig {
  apiUrl: string;
  apiEndpointToken: string;
  stream?: boolean;
  userId?: string;
  infoHtml?: string;
  assistantImage?: string;
  userImage?: string;
  termsUrl?: string;
  lowerHtml?: string;
  displayConversationList?: boolean;
  displayKnowledgePlus?: boolean;
  inputPlaceholder?: string;
  disableDownload?: boolean;
  referencesSectionTitle?: string;
  referencesSectionCollapsed?: number;
  logoUrl?: string;
  welcomeTitle?: string;
  staticQuestions?: string[];
}

interface Props {
  config: BettyConfig;
  userId?: string;
}

interface State {
  isOpen: boolean;
  isLoaded: boolean;
  showPulse: boolean;
}

export class BettyBot extends React.Component<Props, State> {
  private bettyElementId = 'betty-bot-widget';
  private containerRef = React.createRef<HTMLDivElement>();

  constructor(props: Props) {
    super(props);
    this.state = {
      isOpen: false,
      isLoaded: false,
      showPulse: true,
    };
  }

  public componentDidMount() {
    this.loadBettyWidget();
  }

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.userId !== this.props.userId) {
      this.updateUserId();
    }
  }

  private toggleChat = () => {
    this.setState({ 
      isOpen: !this.state.isOpen,
      showPulse: false 
    });
  };

  private loadBettyWidget() {
    const { config, userId } = this.props;
    
    if (document.getElementById(this.bettyElementId)) {
      this.setState({ isLoaded: true });
      return;
    }

    const bettyElement = document.createElement('betty-bot');
    bettyElement.id = this.bettyElementId;
    bettyElement.className = 'demo-betty rasa-betty-bot-widget';
    
    bettyElement.setAttribute('api-url', config.apiUrl);
    bettyElement.setAttribute('api-endpoint-token', config.apiEndpointToken);
    bettyElement.setAttribute('stream', config.stream !== false ? '1' : '0');
    
    if (userId || config.userId) {
      bettyElement.setAttribute('user-id', userId || config.userId || '');
    }
    
    // Generate info-html based on config or use provided one
    let infoHtml = config.infoHtml;
    
    if (!infoHtml) {
      const logoUrl = config.logoUrl || 'https://meetbetty.ai/hs-fs/hubfs/Betty%20Website%20Images%20Used/Betty_RGB%201%20(1).png?width=260&height=105&name=Betty_RGB%201%20(1).png';
      const welcomeTitle = config.welcomeTitle || 'Try asking me questions like:';
      const defaultQuestions = [
        'Hey Betty, please introduce yourself',
        'What kinds of things can you help me with?'
      ];
      const staticQuestions = config.staticQuestions || defaultQuestions;
      
      const questionsHtml = staticQuestions.map(question => 
        `<div class='static-question'>${question}</div>`
      ).join('');
      
      infoHtml = `
        <div class='hero-image'>
          <img alt='logo' src='${logoUrl}'/>
        </div>
        <h3>${welcomeTitle}</h3>
        <br>
        ${questionsHtml}
      `;
    }
    
    bettyElement.setAttribute('info-html', infoHtml);
    
    // Set default assistant and user images
    bettyElement.setAttribute('assistant-image', config.assistantImage || 'https://files.bettybot.ai/ui-images/bettyonbetty/BettyOnBetty-assistant.svg');
    bettyElement.setAttribute('user-image', config.userImage || 'https://files.bettybot.ai/ui-images/bettyonbetty/BettyOnBetty-user.svg');
    bettyElement.setAttribute('terms-url', config.termsUrl || 'https://bettybot.ai/betty-bot-terms-of-use/');
    
    if (config.lowerHtml) {
      bettyElement.setAttribute('lower-html', config.lowerHtml);
    }
    
    if (config.displayConversationList) {
      bettyElement.setAttribute('display-conversation-list', '1');
    }
    
    if (config.displayKnowledgePlus) {
      bettyElement.setAttribute('display-knowledge-plus', '1');
    }
    
    if (config.inputPlaceholder) {
      bettyElement.setAttribute('input-placeholder', config.inputPlaceholder);
    }
    
    if (config.disableDownload) {
      bettyElement.setAttribute('disable-download', '1');
    }
    
    if (config.referencesSectionTitle) {
      bettyElement.setAttribute('references-section-title', config.referencesSectionTitle);
    }
    
    if (config.referencesSectionCollapsed !== undefined) {
      bettyElement.setAttribute('references-section-collapsed', config.referencesSectionCollapsed.toString());
    }

    if (this.containerRef.current) {
      this.containerRef.current.appendChild(bettyElement);
      this.setState({ isLoaded: true });
    }
  }

  private updateUserId() {
    const bettyElement = document.getElementById(this.bettyElementId);
    if (bettyElement && this.props.userId) {
      bettyElement.setAttribute('user-id', this.props.userId);
    }
  }

  public componentWillUnmount() {
    const bettyElement = document.getElementById(this.bettyElementId);
    if (bettyElement) {
      bettyElement.remove();
    }
  }

  public render() {
    const { isOpen, isLoaded, showPulse } = this.state;
    
    return (
      <>
        <div 
          className={`betty-chat-widget ${isOpen ? 'open' : ''}`}
          ref={this.containerRef}
          style={{ display: isOpen && isLoaded ? 'block' : 'none' }}
        />
        
        <button
          className={`betty-chat-launcher ${showPulse ? 'pulse' : ''}`}
          onClick={this.toggleChat}
          aria-label={isOpen ? "Close chat" : "Open chat"}
          title={isOpen ? "Close chat" : "Chat with us"}
        >
          {isOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <ScoutIcon svgwidth={32} svgheight={38} />
          )}
        </button>
      </>
    );
  }
}